import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import LanguageContext from '../../context/LanguageContext.js';
import { useStorePathPrefix } from '../../lib/storefront/storePath.js';
import { usePublicSettings } from '../../context/PublicSettingsContext.js';
import { fetchEvents } from '../../api/eventApi.js';
import { EVENTS_PLACEHOLDER } from '../../data/eventsPlaceholder.js';
import { pickLocalized } from '../../utils/localizedContent.js';
import './Events.css';

function formatEventDate(isoDate, language) {
    try {
        const raw = typeof isoDate === 'string' ? isoDate : new Date(isoDate).toISOString();
        const day = raw.slice(0, 10);
        return new Intl.DateTimeFormat(language || 'en', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        }).format(new Date(`${day}T12:00:00`));
    } catch {
        return String(isoDate || '');
    }
}

function isPageEnabled(slug, publicSettings) {
    if (!publicSettings?.storefrontPages || typeof publicSettings.storefrontPages !== 'object') {
        return true;
    }
    const entry = publicSettings.storefrontPages[slug];
    if (!entry || typeof entry !== 'object') return true;
    return entry.enabled !== false;
}

function mapApiEvent(event, languageKey) {
    const dateValue = event.eventDate || event.date;
    return {
        id: event._id || event.id,
        time: event.eventTime || event.time || '',
        imageUrl: event.imageUrl || '',
        title: pickLocalized(event.title, languageKey) || '',
        description: pickLocalized(event.description, languageKey) || '',
        location: pickLocalized(event.location, languageKey) || '',
        dateLabel: formatEventDate(dateValue, languageKey),
    };
}

function mapPlaceholderEvent(event, t, languageKey) {
    return {
        id: event.id,
        time: event.time,
        imageUrl: '',
        title: t(`events.items.${event.id}.title`),
        description: t(`events.items.${event.id}.description`),
        location: t(`events.items.${event.id}.location`),
        dateLabel: formatEventDate(event.date, languageKey),
    };
}

const Events = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const storePrefix = useStorePathPrefix();
    const { publicSettings } = usePublicSettings();
    const languageKey = i18n.language?.split('-')[0] || 'en';
    const langPrefix = `${storePrefix}/${i18n.language}`;

    const [apiEvents, setApiEvents] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const data = await fetchEvents();
                if (!cancelled) {
                    setApiEvents(Array.isArray(data) ? data : []);
                }
            } catch {
                if (!cancelled) setApiEvents(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const events = useMemo(() => {
        if (Array.isArray(apiEvents) && apiEvents.length > 0) {
            return apiEvents.map((event) => mapApiEvent(event, languageKey));
        }
        if (apiEvents === null) {
            return EVENTS_PLACEHOLDER.map((event) =>
                mapPlaceholderEvent(event, t, languageKey)
            );
        }
        return [];
    }, [apiEvents, languageKey, t]);

    const showReservationCta = isPageEnabled('reservation', publicSettings);
    const showContactCta = isPageEnabled('contact', publicSettings);

    return (
        <main className="main">
            <Breadcrumb title={t('nav.events')} />

            <section className="events-section" dir={dir()}>
                <div className="container events-inner">
                    <div className="events-eyebrow">
                        <span className="events-eyebrow-line" />
                        <span className="events-eyebrow-text">{t('events.eyebrow')}</span>
                    </div>

                    <h1 className="events-heading">{t('events.title')}</h1>
                    <p className="events-intro">{t('events.intro')}</p>

                    {loading ? (
                        <p className="events-empty">{t('events.loading', { defaultValue: 'Loading events…' })}</p>
                    ) : events.length === 0 ? (
                        <p className="events-empty">{t('events.empty')}</p>
                    ) : (
                        <ul className="events-list">
                            {events.map((event) => (
                                <li key={event.id} className="events-item">
                                    {event.imageUrl ? (
                                        <div className="events-media">
                                            <img
                                                src={event.imageUrl}
                                                alt={event.title}
                                                className="events-image"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </div>
                                    ) : null}
                                    <div className="events-date-block" aria-hidden="true">
                                        <span className="events-date-label">{event.dateLabel}</span>
                                        {event.time ? (
                                            <span className="events-time">{event.time}</span>
                                        ) : null}
                                    </div>
                                    <div>
                                        <h2 className="events-item-title">{event.title}</h2>
                                        {event.description ? (
                                            <p className="events-item-desc">{event.description}</p>
                                        ) : null}
                                        {event.location ? (
                                            <p className="events-item-meta">{event.location}</p>
                                        ) : null}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {(showReservationCta || showContactCta) && (
                        <div className="events-cta">
                            <p className="events-cta-text">{t('events.ctaText')}</p>
                            <div className="events-cta-actions">
                                {showReservationCta && (
                                    <Link to={`${langPrefix}/reservation`} className="events-cta-link">
                                        {t('events.ctaReservation')}
                                    </Link>
                                )}
                                {showContactCta && (
                                    <Link
                                        to={`${langPrefix}/contact`}
                                        className="events-cta-link events-cta-link--ghost"
                                    >
                                        {t('events.ctaContact')}
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
};

export default Events;
